/**
 * API client for BRING YOUR PROJECT — the project-scoped import surface
 * (backend #21, `routes/project_import.py`, contract `project-import-1`).
 *
 * Six endpoints, three kinds, one shape: every one is
 * `multipart/form-data`, every preview writes nothing, and every commit
 * re-reads and re-validates the whole file from scratch.
 *
 *   POST /api/import/projects/{id}/schedule/preview      file
 *   POST /api/import/projects/{id}/schedule/commit       file + confirm_hash
 *   POST /api/import/projects/{id}/procurement/preview   file
 *   POST /api/import/projects/{id}/procurement/commit    file + confirm_hash
 *   POST /api/import/projects/{id}/requirements/preview  file
 *   POST /api/import/projects/{id}/requirements/commit   file + confirm_hash
 *
 * THE FIELD NAME IS `confirm_hash`, IN SNAKE CASE.
 *
 * The preview response advertises `"commitRequires": "confirmHash"` in camel
 * case, and it is wrong: `commit_*_import` declares `confirm_hash: str =
 * Form(...)` with no alias, so FastAPI reads the parameter name verbatim off
 * the wire. A client that believes `commitRequires` gets a 422 for a missing
 * required form field and never reaches the 409 it was trying to avoid. The
 * advertised value is deliberately ignored here.
 *
 * Availability differs BY KIND, which is why there is no shared gate:
 *   schedule      role only  (admin/pm/superintendent + owner)
 *   procurement   role only  (admin/pm/estimator + owner)
 *   requirements  role (admin/pm/superintendent + owner)
 *                 + tenant entitlement `requirements_intelligence`
 *                 + global flag OMNAS_REQUIREMENTS_API_ENABLED
 *
 * So requirements can refuse with 403 tenant_not_entitled or 503 not_enabled
 * where the other two never can, and the UI must not present one kind's
 * availability as the surface's.
 */
import apiClient from './api';
/**
 * The caps and the kind vocabulary live in the presentation core, which is
 * dependency-free and therefore drivable by the test harness. Transport
 * imports domain, never the reverse.
 */
import { IMPORT_LIMITS, IMPORT_CONTRACT_VERSION } from '../features/import/importPresentation';

export const PROJECT_IMPORT_CONTRACT = IMPORT_CONTRACT_VERSION;

const rowCapFor = (kind) =>
  kind === 'requirements' ? IMPORT_LIMITS.maxRequirementRows : IMPORT_LIMITS.maxRows;

const importPath = (projectId, kind, action) =>
  `/import/projects/${projectId}/${kind}/${action}`;

/**
 * THE CONTENT-TYPE OVERRIDE IS NOT OPTIONAL.
 *
 * `apiClient` is created with a hard `Content-Type: application/json`
 * default (services/api.js). In axios 1.x, `transformRequest` checks that
 * header BEFORE the adapter ever sees the body:
 *
 *     if (isFormData) {
 *       return hasJSONContentType ? JSON.stringify(formDataToJSON(data)) : data;
 *     }
 *
 * So an un-overridden FormData is collapsed into a JSON object, the `File`
 * becomes `{}` under `JSON.stringify`, and the file bytes never leave the
 * browser. The request that arrives satisfies neither `file: UploadFile =
 * File(...)` nor `confirm_hash: str = Form(...)`, and every one of the six
 * endpoints answers 422 — a total failure that reads like a transient one.
 *
 * Naming the type per request makes `hasJSONContentType` false, so axios
 * keeps the FormData intact and substitutes the real boundary itself. This
 * is what `documentsService.js` already does for its own upload.
 */
const MULTIPART = { headers: { 'Content-Type': 'multipart/form-data' } };

const upload = async (path, file, confirmHash) => {
  const form = new FormData();
  form.append('file', file);
  if (confirmHash !== undefined && confirmHash !== null) {
    // Snake case. See the module header — this is the load-bearing detail.
    form.append('confirm_hash', confirmHash);
  }
  const { data } = await apiClient.post(path, form, MULTIPART);
  return data;
};

const projectImportApi = {
  rowCapFor,

  /**
   * Preview one file. Writes nothing, takes no lock, and returns HTTP 200
   * even when every row is broken — `readyToCommit` and `rowsWithIssues`
   * carry that verdict, never the status code.
   */
  preview: async (projectId, kind, file) => upload(importPath(projectId, kind, 'preview'), file),

  /**
   * Commit one file. `confirmHash` must be the `fileSha256` the preview
   * returned for these exact bytes; the server re-hashes what it receives and
   * refuses with 409 `preview_mismatch` on any difference.
   *
   * The commit does not consult the preview — there is no server-side preview
   * record — so the hash proves the file did not change, not that a preview
   * ran. Every cap and every validation runs again here.
   */
  commit: async (projectId, kind, file, confirmHash) =>
    upload(importPath(projectId, kind, 'commit'), file, confirmHash),
};

export default projectImportApi;
